'use client'

import { GetProjectFiltersQuery } from '@/gql/generated/graphql'
import {
  BodyText,
  DisplayText,
  FilterIcon,
  LeftCaretIcon,
  ListBulletsIcon,
  SquaresFourIcon
} from 'design-configuration/components'
import { Button } from '@components/button'
import React from 'react'
import { ValidArtistsView, useArtistsSortAndFilter } from './use-artists-filters'
import { ArtistListGridItem } from './list-grid-item'
import { ArtistGridItemProps } from './types'
import { ArtistImageGridItem } from './image-grid-item'
import { TrackedButtonNames, trackButtonClicked } from '@utils/analytics'
import { LayoutGroup, motion } from 'motion/react'
import { CommonFilterPanel, FilterPanel } from '@components/grid/filter-panel'
import { ArtistItemInfo } from '@utils/artists'

type ArtistGridProps = {
  artistsInfo: ArtistItemInfo[]
  projectFiltersData?: GetProjectFiltersQuery
  disableImageGrid?: boolean
}

export interface OrganizedListItems {
  [key: string]: ArtistGridItemProps[]
}

const ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'
export const ArtistGrid = ({
  artistsInfo,
  projectFiltersData,
  disableImageGrid
}: ArtistGridProps): React.ReactElement => {
  const { view, setView, numberOfActiveFilters, clearFilters, categoryFilter } = useArtistsSortAndFilter(
    projectFiltersData,
    disableImageGrid
  )

  const [openFilter, setOpenFilter] = React.useState(false)

  const filteredArtists = artistsInfo.filter((item) => {
    if (categoryFilter.selectedOptions.length === 0) {
      return true
    }
    return categoryFilter.selectedOptions.some((selectedOption) => {
      return item.projects?.some((p) => {
        return p?.tags?.some((t) => t.tag_name === selectedOption) || p?.vertical_name === selectedOption
      })
    })
  })

  const organizedListItems: OrganizedListItems = {}

  for (const item of filteredArtists) {
    const letter = item.name?.charAt(0).toUpperCase() ?? ''
    organizedListItems[letter] = [
      ...(organizedListItems[letter] ?? []),
      {
        artist: item
      }
    ]
  }

  const sortedKeys = Object.keys(organizedListItems).sort((a, b) => {
    // If both are letters, sort alphabetically
    if (/^[A-Z]$/.test(a) && /^[A-Z]$/.test(b)) {
      return a.localeCompare(b)
    }
    // If only one is a letter, letter comes first
    if (/^[A-Z]$/.test(a)) return -1
    if (/^[A-Z]$/.test(b)) return 1
    // If neither is a letter, sort alphabetically
    return a.localeCompare(b)
  })

  const totalItems = filteredArtists.length

  const alphabet = sortedKeys.length > 26 ? sortedKeys : ALPHABET.split('')

  return (
    <div>
      <div className="border-outline-variant bg-surface-low py-sm lg:px-xl gap-x-md scrollbar-hidden flex flex-row flex-nowrap justify-between overflow-scroll border-b lg:w-full lg:gap-x-0">
        {alphabet.map((letter) => (
          <button
            onClick={() => {
              document.getElementById(letter)?.scrollIntoView({ behavior: 'smooth' })
            }}
            key={letter}
            className={
              organizedListItems[letter]?.length
                ? 'text-on-surface'
                : 'text-on-surface-variant pointer-events-none opacity-50'
            }
          >
            <BodyText className="font-medium">{letter}</BodyText>
          </button>
        ))}
      </div>

      <div className="px-md py-md flex flex-row items-center justify-between">
        <div className="gap-x-sm flex w-full items-center justify-start">
          <LayoutGroup>
            <motion.div layout="position" className="w-fit">
              <Button
                colorTheme="outline"
                onClick={() => {
                  setOpenFilter(!openFilter)
                }}
              >
                {openFilter ? (
                  <motion.span
                    key="Hide Filter Button"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="gap-x-xs flex items-center justify-center"
                  >
                    <LeftCaretIcon className="w-sm h-sm fill-on-primary-inverted" />
                    <BodyText>Hide filters</BodyText>
                  </motion.span>
                ) : (
                  <motion.span
                    key="Filter Button"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="gap-x-xs flex items-center justify-center"
                  >
                    <FilterIcon className="w-sm h-sm fill-on-primary-inverted" />
                    <BodyText>Filter</BodyText>
                  </motion.span>
                )}
              </Button>
            </motion.div>
            <motion.div layout="position" className="text-on-surface hidden md:block" transition={{ duration: 0.15 }}>
              <BodyText>
                {`${numberOfActiveFilters !== 0 ? `Filtering artists` : `All artists`} (${totalItems} result${
                  totalItems !== 1 ? 's' : ''
                })`}
              </BodyText>
            </motion.div>
            {numberOfActiveFilters !== 0 ? (
              <motion.button
                layout="position"
                className="gap-x-xs text-on-surface-variant rounded-xs hidden items-center underline sm:flex"
                onClick={clearFilters}
                transition={{ duration: 0.15 }}
              >
                <BodyText>Remove all filters</BodyText>
              </motion.button>
            ) : null}
          </LayoutGroup>
        </div>
        {!disableImageGrid ? (
          <div className="rounded-xs border-outline-variant flex flex-row whitespace-nowrap border">
            <button
              onClick={() => {
                setView(ValidArtistsView.list)
                trackButtonClicked(TrackedButtonNames.artistGridViewToggle, {
                  customProperties: {
                    'Section name': ValidArtistsView.list
                  }
                })
              }}
              className={`${
                view === ValidArtistsView.list ? 'bg-primary-8' : 'hover:bg-surface-low bg-surface'
              } px-sm py-xs border-r-outline-variant border-r`}
            >
              <div className="gap-x-xs flex flex-row">
                <ListBulletsIcon className="fill-on-surface" />
                <BodyText className="text-on-primary-inverted hidden sm:flex">View list</BodyText>
              </div>
            </button>
            <button
              onClick={() => {
                setView(ValidArtistsView.images)
                trackButtonClicked(TrackedButtonNames.artistGridViewToggle, {
                  customProperties: {
                    'Section name': ValidArtistsView.images
                  }
                })
              }}
              className={`${
                view === ValidArtistsView.images ? 'bg-primary-8' : 'hover:bg-surface-low bg-surface'
              } px-sm py-xs`}
            >
              <div className="gap-x-xs flex flex-row">
                <SquaresFourIcon className="fill-on-surface" />
                <BodyText className="text-on-primary-inverted hidden sm:flex">View images</BodyText>
              </div>
            </button>
          </div>
        ) : null}
      </div>
      <div className="px-md pb-2xl relative flex h-full w-full">
        {openFilter ? (
          <FilterPanel
            filterLayout={<CommonFilterPanel filters={[categoryFilter]} />}
            filterOpen={openFilter}
            itemCount={totalItems}
            numActiveFilters={numberOfActiveFilters}
            clearFilter={clearFilters}
            onFilterClose={() => setOpenFilter(false)}
          />
        ) : null}
        <div className="flex w-full flex-col">
          {sortedKeys.map((letter, index) => {
            const itemsForLetter = organizedListItems[letter]
            const isLastGroup = index === sortedKeys.length - 1
            if (itemsForLetter?.length === 0) {
              return null
            }
            return (
              <>
                {view === ValidArtistsView.list ? (
                  <div key={letter} className={`relative ${!isLastGroup ? 'border-outline-variant border-b' : ''}`}>
                    <div id={letter} className="absolute -top-[184px] h-2 w-2"></div>
                    <div className="py-2xl gap-x-md px-md flex items-baseline">
                      <DisplayText className="w-[120px] font-medium md:w-[200px]">{letter}</DisplayText>
                      <div className="gap-y-md relative grid w-full grid-cols-1 lg:grid-cols-2">
                        {itemsForLetter?.map((item, index) => (
                          <ArtistListGridItem item={item} key={`list-${item.artist.name}-${index}`} />
                        ))}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div key={letter} className={`relative ${!isLastGroup ? 'border-outline-variant border-b' : ''}`}>
                    <div id={letter} className="absolute -top-[184px] h-2 w-2"></div>
                    <div className="py-2xl gap-x-xl px-md flex flex-col items-start md:flex-row">
                      <DisplayText className="w-[120px] font-medium md:w-[200px]">{letter}</DisplayText>
                      <div className="gap-lg grid w-full grid-cols-2 lg:grid-cols-3">
                        {itemsForLetter?.map((item, index) => (
                          <ArtistImageGridItem item={item} key={`images-${item.artist.name}-${index}`} />
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </>
            )
          })}
        </div>
      </div>
    </div>
  )
}
